<script setup lang="ts">
/* eslint-disable max-lines */
import {
  activateDeviceInfoDaoApi,
  updateDeviceInfoDaoApi
} from '@/accessors/api/requests/disasterCommonDeviceApiGwFuncs'
import { AuthDeviceDB } from '@/accessors/indexedDB/authDeviceDB'
import { qrCodeSample } from '@/assets/img/imageBase64'
import MiiaFwConfig from '@/configs/config'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useAlertMessage } from '@/modules/miiaFw/composables/useAlertMessage'
import { getUserAttributesFromStore, signout } from '@/modules/miiaFw/MiiaFwCognito'
import type {
  ArgActivateDeviceInfo,
  ArgUpdateDeviceInfo
} from '@/types/accessor/api/device/deviceInfoTypes'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import MiiaFwAlertMessage from '@/components/miiafw/utils/MiiaFwAlertMessage.vue'
import { QrcodeStream, type DetectedBarcode } from 'vue-qrcode-reader'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { DEVICE_NAME_MAX_LENGTH, REMARKS_MAX_LENGTH } from '@/constants/views/deviceAuth'

/** 入力データのインターフェイス */
interface InputData {
  deviceId: string
  deviceToken: string
  deviceName: string
  memo: string
}

const commonModalStore = useCommonModalStore()

const qrCodeImg = ref(`data:image/jpg;base64,${qrCodeSample}`)
const router = useRouter()
const errorMsg = ref<string>('')
const authDeviceDB = AuthDeviceDB.instance
const isQrcodeStreamOpen = ref<boolean>(false)
const inputData = ref<InputData>({ deviceId: '', deviceToken: '', deviceName: '', memo: '' })
const activateCode = ref<string>('')
const isError = ref<boolean>(false)
const errorCode = ref<number>(0)

const { addAlertMessage } = useAlertMessage()

/** API実行時のエラーメッセージ */
const deviceErrorMessage: number[] = [
  Number(ERROR_CODES.DEVICE_REGISTER_EXPIRED),
  Number(ERROR_CODES.DEVICE_REGISTER_ALREADY_USED)
]

// 二次元コード読み取りフラグ
const isScanned = computed(() => activateCode.value !== '')

// デバイス登録処理中フラグ
const isProcessing = ref<boolean>(false)

/**
 * 二次元コード読み取り処理
 * @param content デバイス検証のレスポンス
 * @returns なし
 */
const onDetect = async (detectedCodes: DetectedBarcode[]) => {
  activateCode.value = detectedCodes[0]?.rawValue ?? ''
  isQrcodeStreamOpen.value = false
}
/**
 * サインインページに戻る
 * @param なし
 * @returns なし
 */
const backPage = () => {
  if (router.currentRoute.value.path !== ROUTER_PATHS.login)
    router.push({ path: ROUTER_PATHS.login }) // 同じページに遷移させるとNavigation Errorになる。
}
/**
 * デバイスを登録する
 * @param なし
 * @returns なし
 */
const register = async () => {
  isProcessing.value = true
  const attributes = await getUserAttributesFromStore()
  const errorList = validateInputData(inputData.value)
  if (errorList.length > 0) {
    addAlertMessage(errorList.join('\n'))
    isProcessing.value = false
    return
  }

  // リクエストパラメータ生成
  const activateParam: ArgActivateDeviceInfo = {
    system: MiiaFwConfig.config.sysId,
    govCode: attributes.attribute[MiiaFwConfig.config.disasterEmergency.commonAttr].govCode,
    activateCode: activateCode.value
  }
  // アクティベーションAPI呼び出し
  await activateDeviceInfoDaoApi(activateParam)
    .then(async result => {
      isQrcodeStreamOpen.value = false
      inputData.value.deviceToken = result.deviceToken
      inputData.value.deviceId = result.deviceId
      // 成功したら、デバイストークンをIDBにいれる
      await authDeviceDB.insertItem({ deviceToken: result.deviceToken })
    })
    .catch(err => {
      const resCode = err.message.errorCode
      isProcessing.value = false
      if (resCode in deviceErrorMessage) {
        isError.value = true
        errorCode.value = resCode
        isQrcodeStreamOpen.value = false
        return
      }
      // それ以外の場合
      console.error(err)
      throw new GlobalError([ERROR_CODES.DEVICE_REGISTER_DEVICE_ACTIVATION_FAILED])
    })

  // コードの有効期限切れや既に使われてる場合のエラー
  if (isError.value) {
    isProcessing.value = false
    isError.value = false
    activateCode.value = ''
    throw new GlobalError([errorCode.value.toString()])
  }
  const params: ArgUpdateDeviceInfo = {
    system: MiiaFwConfig.config.sysId,
    govCode: attributes.attribute[MiiaFwConfig.config.disasterEmergency.commonAttr].govCode,
    deviceId: inputData.value.deviceId,
    deviceToken: inputData.value.deviceToken,
    deviceName: inputData.value.deviceName,
    memo: inputData.value.memo
  }
  // アップデートAPI呼び出し
  await updateDeviceInfoDaoApi(params)
    .then(() => {
      commonModalStore
        .openAlertModal({
          title: 'デバイス登録完了',
          body: 'デバイス登録が正常に完了しました。',
          okTitle: '閉じる'
        })
        .then(() => {
          if (router.currentRoute.value.path !== ROUTER_PATHS.login)
            router.push({ path: ROUTER_PATHS.login })
        })
    })
    .catch(err => {
      console.error(err)
      throw new GlobalError([ERROR_CODES.DEVICE_REGISTER_DEVICE_UPDATE_FAILED])
    })
  signout().then(() => {
    if (router.currentRoute.value.path !== ROUTER_PATHS.login)
      router.push({ path: ROUTER_PATHS.login }) // 同じページに遷移させるとNavigation Errorになる。
  })
}
/**
 * 2次元コードを読み取るページを開く
 * @param なし
 * @returns なし
 */
const openQrcodeStream = () => {
  isQrcodeStreamOpen.value = true
}
/**
 * 入力データをチェックする
 * @param {InputData} inputData チェック対象の入力情報
 * @returns {string[]} チェックエラー時のメッセージ文
 */
const validateInputData = (inputData: InputData): string[] => {
  const result = validateLength(inputData)
  return result.filter(msg => msg.length > 0)
}
/**
 * 文字数をチェックする
 * @param {InputData} inputData チェック対象の入力情報
 * @returns {string[]} エラーメッセージ
 */
const validateLength = (inputData: InputData): string[] => {
  const alertMessagelist: string[] = []
  if (inputData.deviceName.length === 0) {
    alertMessagelist.push('・デバイス名称は入力必須です。')
  }
  if (inputData.deviceName.length > DEVICE_NAME_MAX_LENGTH) {
    alertMessagelist.push(`・デバイス名称は${DEVICE_NAME_MAX_LENGTH}文字以内です。`)
  }
  if (inputData.memo !== undefined && inputData.memo.length > REMARKS_MAX_LENGTH) {
    alertMessagelist.push(`・備考は${REMARKS_MAX_LENGTH}文字以内です。`)
  }
  return alertMessagelist
}
/**
 * 読み取り端末の整合性チェック
 * @param promise
 * @returns なし
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onInit = async (promise: any) => {
  try {
    await promise
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    switch (error.name) {
      case 'NotAllowedError':
        errorMsg.value = 'ERROR: カメラのアクセス権限を付与する必要があります'
        break
      case 'NotFoundError':
        errorMsg.value = 'ERROR: こちらのデバイスにカメラがありません'
        break
      case 'NotSupportedError':
        errorMsg.value = 'ERROR: セキュリティで保護されたコンテキストが必要です (HTTPS, localhost)'
        break
      case 'NotReadableError':
        errorMsg.value = 'ERROR: カメラは既に使用されていますか?'
        break
      case 'OverconstrainedError':
        errorMsg.value = 'ERROR: インストールされたカメラが適していません'
        break
      case 'StreamApiNotSupportedError':
        errorMsg.value = 'ERROR: Stream APIはこのブラウザーではサポートされていません'
        break
      default: {
        errorMsg.value = 'ERROR: 予期せぬエラーが発生しました'
      }
    }
  }
}
defineExpose({
  errorMsg,
  onDetect,
  onInit,
  backPage,
  register,
  openQrcodeStream,
  isQrcodeStreamOpen,
  inputData,
  qrCodeImg,
  isScanned,
  isProcessing
})
</script>

<template>
  <div class="container">
    <div style="margin-bottom: 5em">
      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 main-area">
          <div class="title">
            デバイス登録
            <hr />
          </div>
          <div class="qr-section">
            <button v-if="!isQrcodeStreamOpen" class="open-qrcode-button" @click="openQrcodeStream">
              <div class="qr-image">
                <img :src="qrCodeImg" class="sample-code" />
                <p class="qr-description">2次元コードを読み取る</p>
              </div>
            </button>
            <QrcodeStream v-if="isQrcodeStreamOpen" @detect="onDetect" @init="onInit" />
          </div>
          <div class="form-row">
            <label class="form-colum-label">2次元コード読み取り【必須】</label>
            <p>{{ isScanned ? '実施済み' : '未実施' }}</p>
          </div>
          <div class="form-row">
            <label class="form-colum-label">デバイス名称【必須】</label>
            <input
              type="text"
              v-model="inputData.deviceName"
              class="form-column-input"
              placeholder="25文字以内で入力してください"
            />
          </div>
          <div class="form-row">
            <label class="form-colum-label">備考</label>
            <textarea
              v-model="inputData.memo"
              style="height: 100px"
              class="form-column-input"
              placeholder="50文字以内で入力してください"
            ></textarea>
          </div>
          <div class="error-area">
            <MiiaFwAlertMessage ref="alertMessage"></MiiaFwAlertMessage>
          </div>
          <div class="button-group">
            <button type="button" class="btn btn-secondary back-top-page" @click="backPage">
              戻る
            </button>
            <button
              type="button"
              class="btn btn-info register-button"
              :disabled="!isScanned || isProcessing"
              @click="register"
            >
              登録する
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.device-container {
  padding: 20px;
}

.title {
  font-size: 25px;
  margin-bottom: 20px;
  position: relative;
  padding-top: 100px;
  padding-bottom: 20px;
}

.title hr {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
}

.qr-section {
  display: flex;
  padding: 20px;
  text-align: center;
  flex-direction: column;
  align-items: center;
  margin-bottom: 50px;
}

.qr-image {
  width: 300px;
  height: 250px;
  border: 2px solid #000;
  display: flex;
  justify-content: flex;
  align-items: center;
  flex-direction: column;
  padding-top: 30px;
  border-radius: 30px;
}

.sample-code {
  width: 150px;
  height: 150px;
}

.qr-description {
  margin-top: 10px;
  font-size: 18px;
}

.form-row {
  display: flex;
  padding-top: 30px;
  height: 80px;
}

.form-colum-label {
  width: 40%;
  margin-right: 5px;
}

.form-column-input {
  width: 50%;
  padding: 10px;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  position: absolute;
  right: 20px;
  padding-top: 80px;
}

.back-top-page,
.register-button {
  padding: 15px;
  margin-left: 10px;
  width: 100px;
}

.open-qrcode-button {
  border: none;
  background: transparent;
  outline: none;
}

.error-area {
  padding-top: 80px;
  padding-right: 60px;
}
</style>
